import type { IImage, IImageForUpload } from 'src/types/image';

import { useCallback } from 'react';

import { Grid } from '@mui/material';

import { ImageViewReplace } from 'src/components/image/image-view-replace';

import { ProfileEditor, type ProfileSchemaType } from './profile-editor';

import type { IProfileCommon } from './types';

export interface ProfileViewProps {
  type?: 'user' | 'organization';
  profile: IProfileCommon;
  onUpdateProfile: (data: Partial<ProfileSchemaType>) => Promise<any>;
  onDeleteImage?: () => Promise<any>;
  onImageUpdated?: (imageGuid: string) => Promise<any>;
  isUpdatingProfile?: boolean;
  isDeletingImage?: boolean;
  disableNameEdit?: boolean;
  nameLabel?: string;
  taglineLabel?: string;
  bioLabel?: string;
  webUrlLabel?: string;
  addFirstImage?: {
    execute: (imageForUpload: IImageForUpload) => Promise<{ guid: string }>;
    loading: boolean;
  };
}

export function ProfileView({
  type = 'user',
  profile,
  onUpdateProfile,
  onImageUpdated,
  onDeleteImage,
  addFirstImage,
  isUpdatingProfile = false,
  isDeletingImage = false,
  disableNameEdit = false,
  nameLabel = 'Name',
  taglineLabel = 'Tagline',
  bioLabel = 'Bio',
  webUrlLabel = 'Web URL',
}: ProfileViewProps) {
  // Handle image replacement

  // Handle image deletion
  const handleDeleteImage = useCallback(async () => {
    if (!onDeleteImage) return;

    try {
      await onDeleteImage();
    } catch (error) {
      console.error('Failed to delete image:', error);
      throw error;
    }
  }, [onDeleteImage]);

  return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ImageViewReplace
            image={profile.image as IImage}
            cropAspect={1 / 1}
            onImageUpdated={onImageUpdated}
            imageForMediaType="user"
            useUploadDialog
            userGuid={type === 'user' ? profile.guid : undefined}
            organizationGuid={type === 'organization' ? profile.guid : undefined}
            remove={
              onDeleteImage
                ? {
                    execute: handleDeleteImage,
                    loading: isDeletingImage,
                  }
                : undefined
            }
            addFirstImage={addFirstImage}
            procStatus={profile.image?.procStatus}
          />
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'top' }}>
          <ProfileEditor
            profile={profile}
            onUpdate={onUpdateProfile}
            isUpdating={isUpdatingProfile}
            disableNameEdit={disableNameEdit}
            nameLabel={nameLabel}
            taglineLabel={taglineLabel}
            bioLabel={bioLabel}
            webUrlLabel={webUrlLabel}
            inDialog
          />
      </Grid>
    </Grid>
  );
}
